import React from 'react';
import { CheckboxCheckedIcon, EditIcon3, DeleteIcon, StarIcon } from 'assets';
import { JobCriteriaData } from '../types';
import { SoftSkillsEditModal, DeleteSectionModal } from 'components';
import { useSoftSkillsEdit, useSoftSkillsDelete, useToast } from 'hooks';
import { SoftSkillsPayload } from 'hooks';
import isEqual from 'lodash/isEqual';
import { toTitleCase } from 'utils/titleCase';

interface SoftSkillsSectionProps {
  assessmentId: string | null;
  data: JobCriteriaData['softSkills'];
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updatedData: JobCriteriaData['softSkills']) => void;
  disabled?: boolean;
}

const initialData = {
  softSkills: []
};

const SoftSkillsSection: React.FC<SoftSkillsSectionProps> = ({
  assessmentId,
  data,
  onEdit,
  onDelete,
  onSave,
  disabled
}) => {
  const { openToast } = useToast();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const {
    submitSoftSkills,
    isSubmitting,
  } = useSoftSkillsEdit(assessmentId);

  const {
    deleteSoftSkills,
  } = useSoftSkillsDelete(assessmentId);

  const onSubmit = (formData: JobCriteriaData['softSkills']) => {
    if (!assessmentId || isSubmitting) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    const payload: SoftSkillsPayload = {
      assessment_id: assessmentId,
      ...formData.softSkills.reduce((acc, skill, index) => ({
        ...acc,
        [`skill${index + 1}`]: skill.title,
        [`skill_expectation${index + 1}`]: skill.description,
        [`skill${index + 1}_rating`]: skill.rating,
        [`check${index + 1}`]: skill.checked
      }), {})
    };

    submitSoftSkills(payload, {
      onSuccess: () => {
        onSave(formData);
        setIsModalOpen(false);
      },
      onError: (_err) => {
        setIsModalOpen(false);
      },
    });
  };

  const handleDeleteConfirm = () => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required for deletion",
        type: "error",
        timeOut: 5000,
      });
      return;
    }

    deleteSoftSkills({ assessment_id: assessmentId }, {
      onSuccess: () => {
        onDelete();
        setIsDeleteModalOpen(false);
      },
    });
  };

  const renderStars = (rating: number, maxRating: number = 5) => {
    return (
      <div className="flex gap-0.5">
        {[...Array(maxRating)].map((_, index) => (
          <span key={index} className={`text-[22px] ${index < rating ? 'text-[#f4b400]' : 'text-[#dfe1e6]'}`}>
            <StarIcon />
          </span>
        ))}
      </div>
    );
  };

  if (isEqual(data, initialData)) return null;
  if (!data) return null;

  return (
    <section className="border-t border-[#dfe1e6] pt-6 mb-8 relative">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl sm:text-2xl font-medium sm:font-semibold m-0">Soft Skills</h2>
        {!disabled && (
          <div className="flex gap-3.5 items-center">
            <span className="cursor-pointer">
              <EditIcon3 onClick={() => { onEdit(); setIsModalOpen(true); }} />
            </span>
            <div className="relative group cursor-pointer">
              <DeleteIcon onClick={() => setIsDeleteModalOpen(true)} />
              <div className="absolute bottom-full right-0 mb-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-52 z-10">
                <span className="block bg-[#1d2939] text-white text-xs rounded-lg px-3.5 py-2.5 leading-relaxed">
                  You can delete section you don't want to screen at this stage
                </span>
                <span className="absolute top-full right-[18px] w-0 h-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-[#1d2939]" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-[35px]">
        {data.softSkills.map((skill, index) => (
          skill.checked && (
            <div key={index} className="flex items-start gap-3">
              <div className="flex gap-4">
                <div className="w-7 h-7 rounded flex items-center justify-center mt-1">
                  <CheckboxCheckedIcon />
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold text-[#172b4d] m-0 mb-1">{toTitleCase(skill.title)}</p>
                  <p className="text-sm text-[#1D2939] m-0 mb-2">{skill.description}</p>
                  <div className="flex mt-1">
                    {renderStars(skill.rating)}
                  </div>
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      <SoftSkillsEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        currentData={data}
        onSave={onSubmit}
        isSubmitting={isSubmitting}
      />

      <DeleteSectionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        sectionName="Soft Skills"
      />
    </section>
  );
};

export default SoftSkillsSection;
